<template>
  <div>
    <base-dialog :active="displayLoginDialog" :title="t('common.My account')" :cancel-text="t('common.Close')"
      @close="enter_inactive" @cancel="enter_inactive">
      <template #title>
        <div class="flex items-center">
          <i class="fas fa-user-alt mr-2"></i>{{ t('common.My account') }}
        </div>
      </template>

      <div class="p-4 space-y-4">
        <p class="text-sm text-stone-600 dark:text-stone-300">
          {{ t('accountdialog.Sign in intro') }}
        </p>

        <div v-if="loading"
          class="flex items-center gap-3 rounded-md border border-stone-200 dark:border-stone-700 bg-stone-50/60 dark:bg-stone-800/40 p-3">
          <i class="fas fa-circle-notch fa-spin text-stone-500"></i>
          <span class="text-stone-600 dark:text-stone-300">{{ t('accountdialog.Preparing sign-in') }}</span>
        </div>

        <div v-if="errorMessage" role="alert"
          class="flex items-start gap-3 rounded-md border border-red-200/70 dark:border-red-700/60 bg-red-50/70 dark:bg-red-900/30 p-3 text-red-700 dark:text-red-200">
          <i class="fas fa-exclamation-triangle mt-0.5"></i>
          <span class="text-sm">{{ errorMessage }}</span>
        </div>

        <div class="rounded-lg border border-stone-200 dark:border-stone-700 bg-white/80 dark:bg-stone-800/60 p-4">
          <div class="space-y-3">
            <button class="btn btn-primary w-full flex items-center justify-center gap-2" @click="continueWithGoogle"
              :disabled="loading">
              <i class="fab fa-google"></i>
              <span>{{ t('common.Sign in with Google') }}</span>
            </button>

            <div class="relative flex items-center">
              <div class="flex-grow border-t border-stone-200 dark:border-stone-700"></div>
              <span class="px-3 text-xs text-stone-500 dark:text-stone-400">{{ t('common.or') }}</span>
              <div class="flex-grow border-t border-stone-200 dark:border-stone-700"></div>
            </div>

            <form @submit.prevent="onSubmit" class="space-y-2">
              <input v-model="email" type="email" required :placeholder="t('common.Email') || 'Email'"
                class="w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800 px-3 py-2 text-sm" />
              <input v-model="password" type="password" required :placeholder="t('common.Password') || 'Password'"
                class="w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800 px-3 py-2 text-sm" />
              <div v-if="authMode === 'signUp'">
                <input ref="displayNameInput" v-model="signupDisplayName" type="text"
                  :aria-invalid="showDisplayNameError ? 'true' : 'false'" required
                  :placeholder="t('common.Display name') || 'Display name'"
                  class="w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800 px-3 py-2 text-sm" />
                <p v-if="showDisplayNameError" class="mt-1 text-xs text-red-600 dark:text-red-300">
                  {{ t('common.Display name is required') }}
                </p>
              </div>
              <div class="flex items-center gap-2">
                <button v-if="authMode === 'signIn'" type="submit" class="btn btn-primary" :disabled="loading">
                  {{ t('common.Sign in') }}
                </button>
                <button v-if="authMode === 'signIn'" type="button"
                  class="ml-auto text-primary-600 hover:text-primary-700 underline disabled:opacity-50 disabled:pointer-events-none text-sm"
                  @click="signUpEmail" :disabled="loading">
                  {{ t('common.Create account') }}
                </button>
                <template v-else>
                  <button type="submit" class="btn btn-primary" :disabled="loading">
                    {{ t('common.Create account') }}
                  </button>
                  <button type="button"
                    class="ml-auto text-primary-600 hover:text-primary-700 underline disabled:opacity-50 disabled:pointer-events-none text-sm"
                    @click="authMode = 'signIn'" :disabled="loading">
                    {{ t('common.Sign in instead') || 'Sign in instead' }}
                  </button>
                </template>
              </div>
            </form>
          </div>
        </div>
      </div>
    </base-dialog>

    <base-dialog :active="displayAccountActionsDialog" :title="t('common.My account')" :cancel-text="t('common.Close')"
      @close="enter_inactive" @cancel="enter_inactive">
      <template #title>
        <div class="flex items-center">
          <i class="fas fa-user-alt mr-2"></i>{{ t('common.My account') }}
        </div>
      </template>

      <div class="p-6 space-y-6">
        <div class="flex flex-col items-center text-center gap-6">
          <img :src="avatarUrl" class="w-20 h-20 rounded-full ring-2 ring-stone-200 dark:ring-stone-700 shadow-sm"
            alt="User avatar">
          <div class="min-w-0">
            <p class="text-xl font-semibold text-stone-800 dark:text-stone-100">
              {{ t('profiledropdown.Signed in as', { name: displayName }) }}
            </p>
            <p v-if="showUserEmail" class="text-sm text-stone-500 dark:text-stone-400">{{ userEmail }}</p>

            <!-- Email verification status and actions -->
            <div class="w-full">
              <div class="mt-2 flex items-center justify-center gap-2">
                <span class="text-xs px-2 py-1 rounded-full" :class="emailVerified
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-200'
                  : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'">
                  <i :class="emailVerified ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'"></i>
                  <span class="ml-1">
                    {{ emailVerified ? t('accountdialog.Email verified') : t('accountdialog.Email not verified') }}
                  </span>
                </span>
              </div>
              <div v-if="!emailVerified" class="mt-2 flex flex-col items-center gap-2">
                <button v-if="canResendVerification" type="button" @click="resendVerificationEmail"
                  class="text-primary-600 hover:text-primary-700 underline disabled:opacity-50 disabled:pointer-events-none text-sm"
                  :disabled="loading" aria-label="Resend verification email">
                  {{ t('accountdialog.Resend verification email') }}
                </button>
              </div>
            </div>
          </div>
          <button class="btn btn-primary w-full" @click="logOut" aria-label="Sign out">
            {{ t('profiledropdown.Sign out') }}
          </button>
        </div>

        <div class="flex flex-wrap justify-center gap-6 mt-1">
          <button type="button" @click="$router.push('/export')"
            class="text-primary-600 hover:text-primary-700 underline disabled:opacity-50 disabled:pointer-events-none text-sm">
            {{ t('profiledropdown.Export my data') }}
          </button>
          <button type="button" @click="confirmAndDelete" :title="t('profiledropdown.Delete account')"
            class="text-red-600 hover:text-red-700 underline disabled:opacity-50 disabled:pointer-events-none text-sm">
            {{ t('profiledropdown.Delete my account') }}
          </button>
        </div>
      </div>
    </base-dialog>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, getCurrentInstance, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUserStore } from '@/stores/useUserStore';
import { useWheelStore } from '@/stores/useWheelStore';
import * as Util from '@utils/Util.js';
import useAuth from '@/composables/useAuth.js';
import { onOpen as onDialogOpen } from '@/services/ui/DialogCoordinator.js';
import BaseDialog from '../shared/BaseDialog.vue';

const { t } = useI18n();
const userStore = useUserStore();
const wheelStore = useWheelStore();
const instance = getCurrentInstance();
const auth = useAuth();

const emit = defineEmits(['start-wait-animation', 'stop-wait-animation', 'show-snackbar-message']);

const fsm = ref('inactive');
const loading = ref(false);
const email = ref('');
const password = ref('');
const signupDisplayName = ref('');
const authMode = ref('signIn'); // 'signIn' | 'signUp'
const showDisplayNameError = ref(false);
const displayNameInput = ref(null);
const errorMessage = ref('');
const isNewUser = ref(false);

// Clear error messages as the user edits fields
watch([email, password], () => {
  if (errorMessage.value) errorMessage.value = '';
});
watch(signupDisplayName, (val) => {
  if (errorMessage.value) errorMessage.value = '';
  if (showDisplayNameError.value && val && val.trim()) {
    showDisplayNameError.value = false;
  }
});

const displayLoginDialog = computed({
  get: () => fsm.value === 'userIsPickingLoginMethod',
  set: (val) => { if (val === false) fsm.value = 'inactive'; }
});

const displayAccountActionsDialog = computed({
  get: () => fsm.value === 'waitingForUserAccountAction',
  set: (val) => { if (val === false) fsm.value = 'inactive'; }
});

const avatarUrl = computed(() => userStore.getUserPhotoUrl);
const userEmail = computed(() => userStore.userEmail);
const showUserEmail = computed(() => {
  const val = userEmail.value;
  return typeof val === 'string' && val.trim().length > 0;
});
const displayName = computed(() => userStore.getDisplayName);

// Email verification state & permissions
const emailVerified = computed(() => userStore.isEmailVerified);
const canResendVerification = computed(() => {
  return userStore.hasPasswordProvider && !emailVerified.value;
});

const show = async () => {
  await enter_loadingLibraries();
};

const enter_loadingLibraries = async () => {
  fsm.value = 'loadingLibraries';
  loading.value = true;
  emit('start-wait-animation');

  try {
    if (await userStore.userIsLoggedInAction()) {
      await enter_waitingForUserAccountAction();
    } else {
      enter_userIsPickingLoginMethod();
    }
  } catch (error) {
    console.error('Error during authentication check:', error);
    errorMessage.value = 'Failed to check authentication status. Please try again.';
    enter_authError(error);
  } finally {
    loading.value = false;
    emit('stop-wait-animation');
  }
};

const enter_inactive = () => {
  fsm.value = 'inactive';
  errorMessage.value = '';
  loading.value = false;
};

const logOut = () => {
  userStore.logOut();
  emit('show-snackbar-message', t('profiledropdown.Signed out'));
  enter_inactive();
};

const confirmAndDelete = () => {
  const ui = instance?.appContext.config.globalProperties.$ui || window.$ui;
  ui?.dialog.confirm({
    title: t('profiledropdown.Delete account'),
    message: t('profiledropdown.Are you sure you want to delete your account'),
    cancelText: t('common.Cancel'),
    confirmText: t('common.Delete'),
    type: 'is-danger',
    hasIcon: true,
    onConfirm: () => deleteAccount()
  });
};

const deleteAccount = async () => {
  loading.value = true;
  emit('start-wait-animation');
  try {
    await userStore.deleteAccount();
    wheelStore.resetWheel();
    const msg = t('profiledropdown.Your account and your saved wheels have been deleted');
    emit('show-snackbar-message', msg);
    enter_inactive();
  } catch (error) {
    // No user-facing error; logged elsewhere
  } finally {
    loading.value = false;
    emit('stop-wait-animation');
  }
};

/**
 * Resend verification email to current user (email/password accounts)
 */
const resendVerificationEmail = async () => {
  try {
    loading.value = true;
    await userStore.sendVerificationEmail();
    emit('show-snackbar-message', t('accountdialog.Verification email sent'));
  } catch (e) {
    console.error('Resend verification email error:', e);
    errorMessage.value = t('accountdialog.Verification email failed');
  } finally {
    loading.value = false;
  }
};


const enter_userIsPickingLoginMethod = () => {
  fsm.value = 'userIsPickingLoginMethod';
  errorMessage.value = '';
  authMode.value = 'signIn';
  signupDisplayName.value = '';
  showDisplayNameError.value = false;
};

const enter_waitingForUserAccountAction = async () => {
  fsm.value = 'waitingForUserAccountAction';
  errorMessage.value = '';
  try {
    await userStore.logUserActivity();
  } catch (error) {
    console.warn('Could not log user activity:', error);
  }
};

const enter_authError = (exception) => {
  console.error('Authentication error:', exception);
  if (!errorMessage.value) {
    errorMessage.value = exception && exception.message ? `Authentication error: ${exception.message}` : t('accountdialog.Login failed');
  }
  fsm.value = 'userIsPickingLoginMethod';
};

defineExpose({ show, enter_inactive, logOut, confirmAndDelete });

// New methods for redirect/email flows
const continueWithGoogle = async () => {
  try {
    loading.value = true;
    Util.trackEvent('Wheel', 'LoginForAccountAttempt', 'google');
    const resultUser = await auth.signInWithGoogle();
    if (resultUser) {
      // Popup success path (no redirect)
      const justCreatedAccount = auth.wasUserJustCreated();
      isNewUser.value = justCreatedAccount;
      emit('show-snackbar-message', t('profiledropdown.Signed in success'));
      await auth.postSignInSetup();
      await enter_waitingForUserAccountAction();
      return;
    }
    // Redirect path: no further code runs here
  } catch (ex) {
    // Treat user-cancelled popup as benign (no error UI/state)
    if (ex && (ex.code === 'auth/popup-closed-by-user' || ex.code === 'auth/cancelled-popup-request')) {
      return;
    }
    console.error('Google redirect init error:', ex);
    errorMessage.value = ex && ex.message ? `Login failed: ${ex.message}` : t('accountdialog.Login failed');
  } finally {
    loading.value = false;
  }
};

// Ensure this dialog closes if another dialog is opened
let unsubscribeDialogOpen = null;
onMounted(() => {
  unsubscribeDialogOpen = onDialogOpen((name) => {
    if (name !== 'account') {
      enter_inactive();
    }
  });
});
onBeforeUnmount(() => {
  if (typeof unsubscribeDialogOpen === 'function') {
    try { unsubscribeDialogOpen(); } catch { }
  }
});

const signInEmail = async () => {
  try {
    loading.value = true;
    Util.trackEvent('Wheel', 'LoginForAccountAttempt', 'email');
    const user = await auth.signInWithEmailPassword(email.value.trim(), password.value);
    Util.trackEvent('Wheel', 'LoginForAccountSuccess', 'email');
    const justCreatedAccount = auth.wasUserJustCreated();
    isNewUser.value = justCreatedAccount;
    emit('show-snackbar-message', t('profiledropdown.Signed in success'));
    await auth.postSignInSetup();
    enter_waitingForUserAccountAction();
  } catch (ex) {
    console.error('Email sign-in error:', ex);
    Util.trackEvent('Wheel', 'LoginForAccountFailure', 'email');
    errorMessage.value = ex && ex.message ? `Login failed: ${ex.message}` : t('accountdialog.Login failed');
    if (ex && (ex.code === 'auth/user-not-found' || ex.code === 'auth/invalid-credential')) {
      authMode.value = 'signUp';
      errorMessage.value = '';
      showDisplayNameError.value = false;
      nextTick(() => displayNameInput.value && displayNameInput.value.focus && displayNameInput.value.focus());
    } else {
      enter_authError(ex);
    }
  } finally {
    loading.value = false;
  }
};

const signUpEmail = async () => {
  if (authMode.value === 'signIn') {
    authMode.value = 'signUp';
    errorMessage.value = '';
    showDisplayNameError.value = false;
    nextTick(() => displayNameInput.value && displayNameInput.value.focus && displayNameInput.value.focus());
    return;
  }
  try {
    loading.value = true;
    Util.trackEvent('Wheel', 'LoginForAccountAttempt', 'email-signup');
    if (!signupDisplayName.value || !signupDisplayName.value.trim()) {
      showDisplayNameError.value = true;
      loading.value = false;
      nextTick(() => displayNameInput.value && displayNameInput.value.focus && displayNameInput.value.focus());
      return;
    }
    const user = await auth.registerWithEmailPassword(
      email.value.trim(),
      password.value,
      signupDisplayName.value.trim()
    );
    Util.trackEvent('Wheel', 'LoginForAccountSuccess', 'email-signup');
    isNewUser.value = true;
    emit('show-snackbar-message', t('profiledropdown.Welcome new user'));
    // Inform the user that a verification email was sent
    emit('show-snackbar-message', t('accountdialog.Verification email sent'));
    await auth.postSignInSetup();
    enter_waitingForUserAccountAction();
  } catch (ex) {
    console.error('Email sign-up error:', ex);
    Util.trackEvent('Wheel', 'LoginForAccountFailure', 'email-signup');
    errorMessage.value = ex && ex.message ? `Signup failed: ${ex.message}` : t('accountdialog.Login failed');
    enter_authError(ex);
  } finally {
    loading.value = false;
  }
};

const onSubmit = async () => {
  if (authMode.value === 'signIn') {
    await signInEmail();
  } else {
    await signUpEmail();
  }
};
</script>
